import { FC, useRef } from 'react';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import { Heading, Image, Button, headingType, size } from '@marriott/mi-ui-library';
import { useSession } from '@marriott/mi-headless-utils';

import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import { EditableComponent } from '@adobe/aem-react-editable-components';

import { StyledMerchandisingTileSkinny } from './MerchandisingTileSkinny.styles';
import { MerchandisingTileSkinnyProps } from './MerchandisingTileSkinny.types';
import { getTrackingPropertiesDynamic, isExternalLink } from '../../utils/helpers';

export const MerchandisingTileSkinnyComponent: FC<MerchandisingTileSkinnyProps> = props => {
  const { datalayer } = useSession();
  const {
    mboxParameter,
    metaData,
    openInaNewTab,
    destinationUrl1,
    headerShort,
    headerTag,
    descriptionShort,
    trackingProperties = {},
    image,
    logo,
    imageLoading,
    styleclass,
    iconPath,
    custom_click_track_value = '',
    displayEvergreenContent = true,
    isTargetContentLoaded = mboxParameter && !AuthoringUtils.isInEditor() ? false : true,
    isTargetContentError = false,
  } = props;

  const ref = useRef<HTMLAnchorElement>(null);

  const trackingPropertiesDynamic = getTrackingPropertiesDynamic(
    trackingProperties,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metaData || ({} as any),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props as any,
    datalayer?.['mr_prof_authentication_state'] === 'authenticated',
    isTargetContentError,
    mboxParameter
  );

  const sanitizedNode = DOMPurify.sanitize(descriptionShort, { RETURN_DOM: true });
  const fragmentString = (sanitizedNode as unknown as HTMLElement)?.innerHTML;
  return (
    <StyledMerchandisingTileSkinny
      data-component-name="o-merch-MerchandisingTileSkinny"
      data-testid="merch-MerchandisingTileSkinny"
      openInaNewTab={openInaNewTab}
      className={clsx(`merchandising-tile-skinny col-12 px-md-0 ${styleclass}`, {
        'd-none': isTargetContentError && String(displayEvergreenContent) === 'false',
      })}
      fontIcon={iconPath || ''}
    >
      {isTargetContentLoaded ? (
        <Button
          isLink={true}
          className={clsx(
            'merchandising-tile-skinny__link t-font-alt-s p-3 d-flex align-items-center t-background-color',
            {
              'icon-arrow-right-external': isExternalLink(destinationUrl1, openInaNewTab),
              'icon-arrow-right icon-l': !isExternalLink(destinationUrl1, openInaNewTab),
            }
          )}
          href={destinationUrl1}
          target={isExternalLink(destinationUrl1, openInaNewTab) ? '_blank' : '_self'}
          trackingProperties={trackingPropertiesDynamic}
          rel={`${openInaNewTab ? 'noopener noreferrer' : ''}`}
          ref={ref}
          custom_click_track_value={custom_click_track_value}
        >
          <Image
            customClass={clsx('merchandising-tile-skinny__image ')}
            altText={logo?.['altText'] || image?.['altText']}
            dynamic={false}
            defaultImageURL={logo?.['imageSrc'] || image?.assetPath}
            tabIndex={0}
            loading={imageLoading}
          />

          <div className={clsx('merchandising-tile-skinny__content')}>
            <div className={clsx('pl-3')}>
              <Heading
                titleText={headerShort}
                variation={headingType.subtitle}
                disableCustomClickTrack
                fontSize={size.medium}
                element={headerTag}
                customClass={clsx('m-0 merchandising-tile-skinny__heading')}
              />

              {descriptionShort?.length > 0 && (
                <div
                  dangerouslySetInnerHTML={{ __html: fragmentString }}
                  className={clsx('merchandising-tile-skinny__description t-font-alt-xs pt-1')}
                ></div>
              )}
            </div>
          </div>
        </Button>
      ) : (
        <a className="merchandising-tile-skinny__link t-background-color d-flex align-items-center p-3">
          <div className={'merchandising-tile-skinny__image px-5 py-3 skeleton-loader'} />
          <div className="merchandising-tile-skinny__content col-8">
            <div className="pl-3">
              <span className="d-block py-2 mb-1 skeleton-loader" />
              <div className={'py-2 skeleton-loader'} />
            </div>
          </div>
        </a>
      )}
    </StyledMerchandisingTileSkinny>
  );
};

const MerchandisingTileSkinnyConfig = {
  emptyLabel: 'Tile Skinny',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/tileskinny`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MerchandisingTileSkinny = (props: any) => {
  return (
    <EditableComponent
      config={MerchandisingTileSkinnyConfig}
      {...props}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children={<MerchandisingTileSkinnyComponent {...({} as any)} />}
    />
  );
};
